import { useState } from 'react'
import { Link } from 'react-router-dom'
import { displayAction } from '../../../lib/displayApi'

const MEMBER_COLORS = {
  slate: '#64748b', rose: '#f43f5e', amber: '#f59e0b', emerald: '#10b981',
  sky: '#0ea5e9', violet: '#8b5cf6', pink: '#ec4899', orange: '#f97316',
  teal: '#14b8a6', indigo: '#6366f1',
}

const TABS = ['My Day', 'Tasks', 'Calendar', 'More']

function fmt(iso) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

export function AdultPersonalView({ member, data, deviceToken, onBack }) {
  const [tab, setTab]   = useState(0)
  const [tasks, setTasks] = useState(data.tasks ?? [])
  const memberColor = MEMBER_COLORS[member.memberColor] ?? '#8b5cf6'
  const isAdmin = member.role === 'admin'

  const today = new Date()
  const todayStr = today.toISOString().split('T')[0]
  const tomorrowStr = new Date(today.getTime() + 86400000).toISOString().split('T')[0]

  const myEvents = (data.events ?? [])
    .filter((e) => (e.event_members ?? []).some((em) => em.user_id === member.id))
    .sort((a, b) => a.start_time.localeCompare(b.start_time))

  const todayEvents    = myEvents.filter((e) => e.start_time?.startsWith(todayStr))
  const tomorrowEvents = myEvents.filter((e) => e.start_time?.startsWith(tomorrowStr))

  const myTasks = tasks.filter((t) => t.assigned_to === member.id && !t.completed_at)

  const upcomingBills = (() => {
    if (!isAdmin) return []
    const now = new Date()
    return (data.bills ?? [])
      .map((b) => {
        let days = b.due_day - now.getDate()
        if (days < 0) days += 31
        return { ...b, daysUntil: days }
      })
      .filter((b) => b.daysUntil <= 7)
      .sort((a, b) => a.daysUntil - b.daysUntil)
  })()

  async function completeTask(taskId) {
    try {
      await displayAction(deviceToken, member.id, 'complete_task', taskId)
      setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, completed_at: new Date().toISOString() } : t))
    } catch (_) {}
  }

  const EventList = ({ events, label }) => (
    <div className="mb-4">
      {label && <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2">{label}</p>}
      {events.length === 0 ? (
        <p className="text-gray-600 text-sm px-1">Nothing scheduled</p>
      ) : (
        <div className="space-y-1.5">
          {events.map((e) => (
            <div key={e.id} className="flex items-center gap-3 bg-white/8 rounded-xl px-3 py-2.5">
              <span className="text-gray-400 text-xs w-16 flex-shrink-0">
                {e.is_all_day ? 'All day' : fmt(e.start_time)}
              </span>
              <div className="min-w-0">
                <p className="text-white font-medium text-sm truncate">{e.title}</p>
                {e.location && <p className="text-gray-500 text-xs mt-0.5 truncate">{e.location}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )

  const MyDay = () => (
    <div className="space-y-4">
      <EventList events={todayEvents} label="Today" />
      <EventList events={tomorrowEvents} label="Tomorrow" />

      {upcomingBills.length > 0 && (
        <div>
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2">Bills Due Soon</p>
          <div className="space-y-1.5">
            {upcomingBills.map((b) => (
              <div key={b.id} className="flex items-center justify-between bg-white/8 rounded-xl px-3 py-2.5">
                <span className="text-white font-medium text-sm">{b.name}</span>
                <div className="text-right">
                  <p className="text-white font-bold text-sm">${Number(b.amount).toFixed(2)}</p>
                  <p className="text-gray-500 text-xs">{b.daysUntil === 0 ? 'Today' : `${b.daysUntil}d`}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(data.announcements ?? []).length > 0 && (
        <div>
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2">Announcements</p>
          {data.announcements.slice(0, 2).map((a) => (
            <div key={a.id} className="bg-white/8 rounded-xl px-3 py-2.5 mb-1.5">
              <p className="text-white font-medium text-sm">{a.title}</p>
              <p className="text-gray-400 text-xs mt-0.5 line-clamp-2">{a.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )

  const Tasks = () => (
    <div>
      {myTasks.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-4xl mb-2">✅</div>
          <p className="text-gray-400 text-base">All caught up!</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {myTasks.map((task) => (
            <button
              key={task.id}
              onClick={() => completeTask(task.id)}
              className="w-full flex items-center gap-3 bg-white/8 hover:bg-white/12 rounded-xl px-3 py-2.5 text-left transition-colors"
            >
              <div className="w-5 h-5 rounded border-2 border-gray-500 hover:border-emerald-400 flex-shrink-0 transition-colors" />
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">{task.title}</p>
                {task.due_date && (
                  <p className="text-gray-500 text-xs mt-0.5">
                    Due {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )

  const Calendar = () => {
    let lastDate = ''
    const upcoming = myEvents.filter((e) => new Date(e.start_time) >= today).slice(0, 15)
    return (
      <div>
        {upcoming.map((e) => {
          const d = e.start_time.split('T')[0]
          const show = d !== lastDate
          lastDate = d
          return (
            <div key={e.id}>
              {show && <p className="text-gray-400 text-xs uppercase tracking-wider mt-3 mb-1.5">{new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</p>}
              <div className="flex gap-3 bg-white/8 rounded-xl px-3 py-2.5 mb-1.5">
                <span className="text-gray-500 text-xs w-16 flex-shrink-0 pt-px">{e.is_all_day ? 'All day' : fmt(e.start_time)}</span>
                <p className="text-white text-sm">{e.title}</p>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const More = () => (
    <div className="space-y-2">
      <Link to="/calendar" className="flex items-center gap-3 bg-white/8 hover:bg-white/12 rounded-xl px-4 py-3 transition-colors">
        <span className="text-lg">📅</span>
        <span className="text-white font-medium text-sm">Open Full App</span>
        <span className="ml-auto text-gray-500 text-sm">→</span>
      </Link>
      <Link to="/tasks" className="flex items-center gap-3 bg-white/8 hover:bg-white/12 rounded-xl px-4 py-3 transition-colors">
        <span className="text-lg">✅</span>
        <span className="text-white font-medium text-sm">All Tasks</span>
        <span className="ml-auto text-gray-500 text-sm">→</span>
      </Link>
      {isAdmin && (
        <Link to="/budget" className="flex items-center gap-3 bg-white/8 hover:bg-white/12 rounded-xl px-4 py-3 transition-colors">
          <span className="text-lg">💰</span>
          <span className="text-white font-medium text-sm">Budget</span>
          <span className="ml-auto text-gray-500 text-sm">→</span>
        </Link>
      )}
    </div>
  )

  return (
    <div className="fixed inset-0 overflow-hidden flex flex-col bg-gray-950">
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
            style={{ backgroundColor: memberColor }}
          >
            {member.displaySettings?.avatar_emoji ?? member.displayName?.[0]}
          </div>
          <span className="text-white font-semibold text-sm sm:text-base">{member.displayName}</span>
        </div>
        <button onClick={onBack} className="text-gray-400 hover:text-white text-xs sm:text-sm px-3 py-1.5 rounded-lg bg-white/10 transition-colors">
          ← Back
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/10 flex-shrink-0">
        {TABS.map((t, i) => (
          <button
            key={t}
            onClick={() => setTab(i)}
            className={`flex-1 py-2.5 text-xs sm:text-sm font-semibold transition-colors ${tab === i ? 'text-white border-b-2' : 'text-gray-500'}`}
            style={tab === i ? { borderColor: memberColor } : {}}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-5 min-h-0">
        {tab === 0 && <MyDay />}
        {tab === 1 && <Tasks />}
        {tab === 2 && <Calendar />}
        {tab === 3 && <More />}
      </div>
    </div>
  )
}
